import { Link, Outlet, useLocation } from "react-router-dom";
import { Badge, Button, Container, Nav, Navbar } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setHideFilter } from "../../redux/filtersSlice";
import { RootState } from "../../redux/store";
import FiltersContainer from "../../components/Filters/components/FiltersContainer";
import { useFiltersClaims } from "../../features/claims/hooks/useFiltersClaims";
import useExportToPDF from "../../hooks/useExportToPDF";
import ModalGlobal from "../AppModal/ModalGlobal";
import { useAlert } from "../../hooks/useAlert";
import { addAuditLog } from "../../features/auditLogs/api/auditApi";
import { AuditActionEnum } from "../../features/auditLogs/types";
import { ApiErrorResponse } from "../../types/ApiErrorResponse";

export type DashboardOutletContext = {
    section1Ref: React.RefObject<HTMLDivElement>;
    section2Ref: React.RefObject<HTMLDivElement>;
    section3Ref: React.RefObject<HTMLDivElement>;
    feelingchartRef: React.RefObject<HTMLDivElement>;
    setShowNavigation: (show: boolean) => void;
};
export default function ClaimDashboardLayout() {
    const { t } = useTranslation('claim');
    const location = useLocation();
    const dispatch = useDispatch();
    const { data: filters } = useFiltersClaims();

    const MENU_ITEMS = [
        { name: "Dashboard", link: "/claims/dashboard", notifications: undefined },
        { name: t("claim:historic.menuClaimDataHistory"), link: "/claims/historic", notifications: undefined },
    ];

    useEffect(() => {
        const currentPath = location.pathname;
        const matchingItem = MENU_ITEMS.find((item) => currentPath === item.link);
        if (matchingItem) {
            setSelectedItem(matchingItem.link);
        } else {
            setSelectedItem("/claims/dashboard");
        }
    }, [location.pathname]);

    const [selectedItem, setSelectedItem] = useState<string>("/claims/historic");

    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false)
    const isHideFilter = useSelector((state: RootState) => state.filters.claims.hideFilter);

    const handleClick = () => {
        dispatch(setHideFilter({ table: 'claims', hide: false }));  // Ejecutar el dispatch de la acci?n
        setIsFilterVisible(!isFilterVisible); // Cambiar el estado local
    };
    const { showAlert } = useAlert({ fromLocation: false });


    const handleExportPdf = async () => {
        try {
            if (section1Ref.current && section2Ref.current) {
                exportToPDF();
                await addAuditLog({
                    entity: 'claimDashboard',
                    action: AuditActionEnum.Print,
                    source: window.location.pathname,
                    description: "Full claims dashboard report printed"
                });
                handleClose();
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message

            showAlert({
                message: errorMessage?.toString() ?? t("common:modalPrint.errorToGeneratePdf"),
                type: 'danger',
                duration: 8000
            });
        }
    }

    const { exportToPDF, isExporting, section1Ref, section2Ref, section3Ref } = useExportToPDF(t, 'claims', filters);

    const [showModal, setShowModal] = useState(false);
    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);


    return (
        <Container fluid>
            <ModalGlobal
                show={showModal}
                onClose={handleClose}
                title={t("common:modalPrint.modalShowHistoricalConfirmationPDFTitle")}
                subtitle={t("common:modalPrint.modalShowHistoricalConfirmationPDFDescription", { description: "" })}
                body={<div></div>}
                onSave={() => {
                    handleExportPdf();
                }}
                contentType='info'
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                isLoading={false}
            /> 
            <Navbar bg="white" expand="lg">
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
                <Navbar.Collapse id="basic-navbar-nav">
                    <Nav className="me-auto w-90" variant="tabs" activeKey={MENU_ITEMS.findIndex(item => item.link === selectedItem)}>
                        {MENU_ITEMS.map((item, index) => (
                            <Nav.Link
                                as={Link}
                                to={item.link}
                                key={index}
                                eventKey={index}
                                onClick={() => setSelectedItem(item.link)}
                                className={`position-relative survey-tab-link ${item.link === selectedItem ? "active-tab" : ""}`}
                            >
                                {item.name}
                                {!!item.notifications &&
                                    <Badge bg="success" className="position-absolute top-0 translate-middle badge rounded-pill">
                                        {item.notifications < 10 ? item.notifications : "+9"}
                                    </Badge>
                                }
                            </Nav.Link>
                        ))}
                    </Nav>
                    <Nav className="ms-auto">
                        <Button
                            variant=""
                            onClick={(e) => {
                                e.preventDefault();
                                handleOpen();
                            }}
                            aria-controls="collapse-filter-id"
                            style={{
                                border: 0,
                                visibility: (selectedItem === "/claims/dashboard") ? 'visible' : 'hidden' 
                            }}
                        >
                            <img
                                src="/download.png"
                                alt={isExporting ? 'Generant PDF...' : t(" ButtonExportTitle")}
                                style={{ width: '50px', }}
                            />
                        </Button>
                        <Button variant="" onClick={handleClick} aria-controls="collapse-filter-id" style={{ border: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '50px', }}
                            />
                        </Button> 
                    </Nav>
                </Navbar.Collapse>
            </Navbar>

            <div>
                {!isHideFilter && filters && selectedItem !== "/alertes" && (
                   <div className="dashboard-section">
                        <FiltersContainer
                            filters={filters}
                            isFilterVisible={isFilterVisible && !isHideFilter}
                            tableKey="claims"
                            historicalKey="claim"
                        />
                    </div>
                )}
            </div>
            <div className="justify-content-center p-0">
                <Outlet context={{ section1Ref, section2Ref, section3Ref }} />
            </div>
        </Container>
    );
}